import config from "../../../core/config";
import { postExternalAuthCallback } from "../../../core/services/integration_auth";
import { exchangeOAuthCodeOnce } from "../../../core/services/oauthExchangeDedup";
import { fetchJsonDesktopSafe } from "../../../core/services/desktopFetch";
import { renderConnectorBrandIcon } from "../connectorBrandIcons";
import {
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  buildActionItemsSettingsPath,
} from "../actionItemHelpers";
import {
  buildWizardGmailFiltersPayload,
  DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE,
  INVITE_RULE_NAME,
  MEETING_DEFAULT_KEYWORDS,
  MEETING_DEFAULT_SENDERS,
  MEETING_RULE_NAME,
  splitCommaValues,
  WIZARD_INVITE_SUBJECT_KEYWORDS,
} from "./businessFilterPayload";
import { DEFAULT_LOOKBACK_DAYS } from "./lookbackPeriod";
import {
  DESKTOP_CALLBACK_PATH,
  DESKTOP_STATE_SUFFIX,
  LEGACY_DESKTOP_LOOPBACK_URL,
  LEGACY_DESKTOP_STATE_SUFFIX,
  buildDesktopCallbackPageUrl,
  isDesktopLoginState,
  isDesktopState,
  isLegacyDesktopState,
  readDesktopCallbackParams,
} from "../../../core/utils/desktopCallback";

export const ZOOM_DIGEST_WORKFLOW_TYPE = "zoom_daily_digest";
export const GONG_DIGEST_WORKFLOW_TYPE = "gong_daily_digest";
export const GRANOLA_DIGEST_WORKFLOW_TYPE = "granola_daily_digest";
export const NOTION_DIGEST_WORKFLOW_TYPE = "notion_daily_digest";
export const GOOGLE_CALENDAR_DIGEST_WORKFLOW_TYPE = "google_calendar_daily_digest";
export const GOOGLE_MEET_DIGEST_WORKFLOW_TYPE = "google_meet_daily_digest";
export const SLACK_DIGEST_WORKFLOW_TYPE = "slack_daily_digest";
export { DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE };
export { INVITE_RULE_NAME, MEETING_RULE_NAME, WIZARD_INVITE_SUBJECT_KEYWORDS };

export const BUSINESS_DIGEST_DRAFT_STORAGE_KEY = "business_digest_wizard_draft_v1";

export const DIGEST_SETUP_TRANSIENT_ERROR_MARKERS = [
  "invalid jwt signature",
  "invalid_grant",
  "internal error occurred during connector creation",
  "could not create destination schemas",
  "setup is still finishing in the background",
];

export const DIGEST_SETUP_STILL_INITIALIZING_MESSAGE =
  "Setup is still finishing in the background. Please click Finish setup again.";

/**
 * Return true when a setup failure is likely to succeed on a quick retry.
 *
 * @example
 * isTransientDigestSetupError("invalid_grant: Invalid JWT Signature.");
 * // true
 */
export const isTransientDigestSetupError = (message) =>
  DIGEST_SETUP_TRANSIENT_ERROR_MARKERS.some((marker) =>
    String(message || "").toLowerCase().includes(marker)
  );

/**
 * Map backend setup failures to user-friendly copy.
 *
 * @example
 * formatDigestSetupError("Failed to set up Google Meet workflow: 'error'");
 * // "Setup is still finishing in the background. Please click Finish setup again."
 */
export const formatDigestSetupError = (message) => {
  if (isTransientDigestSetupError(message)) {
    return DIGEST_SETUP_STILL_INITIALIZING_MESSAGE;
  }
  return message || "Setup failed. Please try again.";
};

/** @param {number} ms */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const DIGEST_SETUP_AUTO_RETRY_ATTEMPTS = 3;
export const DIGEST_SETUP_AUTO_RETRY_DELAY_MS = 3000;
export const BUSINESS_RECONNECT_CONTEXT_KEY = "business_reconnect_context_v1";

/**
 * Session storage key scoped to a workflow template.
 *
 * @example
 * getBusinessDigestDraftStorageKey("zoom_daily_digest");
 * // "business_digest_wizard_draft_v1_zoom_daily_digest"
 */
export function getBusinessDigestDraftStorageKey(workflowType) {
  return `${BUSINESS_DIGEST_DRAFT_STORAGE_KEY}_${String(workflowType || "").trim()}`;
}

/** Said by every read-only connector, so the connect screen omits it; shown only when a connector says something different. */
export const GENERIC_NEVER_LINE = "Never edits, shares, or stores anything else";

/**
 * Primary OAuth CTA label for connector setup panels.
 *
 * @example
 * buildConnectCta("recordings");
 * // "Allow access to my recordings"
 */
export const buildConnectCta = (resource) => `Allow access to my ${resource}`;

export const SIMPLE_CONNECT_COPY = {
  gmail: {
    connectCta: buildConnectCta("inbox"),
    accessLine: "Reads meeting emails to find action items",
    neverLine: GENERIC_NEVER_LINE,
  },
  zoom: {
    connectCta: buildConnectCta("recordings"),
    accessLine: "Reads Zoom recordings to find action items",
    neverLine: GENERIC_NEVER_LINE,
  },
  google_meet: {
    connectCta: buildConnectCta("transcripts"),
    accessLine: "Reads Google Meet transcripts to find action items",
    neverLine: GENERIC_NEVER_LINE,
  },
  gong: {
    connectCta: buildConnectCta("calls"),
    accessLine: "Reads Gong call recordings to find action items",
    neverLine: GENERIC_NEVER_LINE,
  },
  google_calendar: {
    connectCta: buildConnectCta("calendar"),
    accessLine: "Reads calendar events to find action items",
    neverLine: GENERIC_NEVER_LINE,
  },
  granola: {
    connectCta: buildConnectCta("notes"),
    accessLine: "Reads Granola notes to find action items",
    neverLine: GENERIC_NEVER_LINE,
  },
  notion: {
    connectCta: buildConnectCta("Notion meetings"),
    accessLine: "Reads your Notion AI meeting notes",
    neverLine: GENERIC_NEVER_LINE,
  },
  slack: {
    connectCta: buildConnectCta("DMs and group DMs"),
    accessLine: "Reads DMs and group DMs to find action items",
    neverLine: "Never reads a channel unless you allow it, never posts on your behalf",
  },
  attio: {
    connectCta: "Allow access to Attio",
    accessLine: "Writes contacts, notes, and follow-ups you confirm",
    neverLine: "Never writes to Attio without your confirmation",
  },
  salesforce: {
    connectCta: "Allow access to Salesforce",
    accessLine: "Writes contacts, notes, and follow-ups you confirm",
    neverLine: "Never writes to Salesforce without your confirmation",
  },
};

/**
 * Privacy copy and CTA label for the simplified connector setup screen.
 *
 * @example
 * getSimpleConnectCopy("zoom").connectCta;
 * // "Allow access to my recordings"
 */
export const getSimpleConnectCopy = (sourceType) =>
  SIMPLE_CONNECT_COPY[sourceType] || SIMPLE_CONNECT_COPY.zoom;

/**
 * Whether Gmail filter fields are filled enough to complete setup.
 *
 * @example
 * canFinishGmailConnectorSetup("no-reply@zoom.us", "meeting");
 * // true
 */
export const canFinishGmailConnectorSetup = (senderPatterns, subjectKeywords) =>
  splitCommaValues(senderPatterns).length > 0 &&
  splitCommaValues(subjectKeywords).length > 0;

/**
 * Whether setup should auto-complete after OAuth without a second click.
 *
 * @example
 * shouldAutoFinishConnectorSetup({
 *   sourceAuthorized: true,
 *   canFinish: true,
 * });
 * // true
 */
export const shouldAutoFinishConnectorSetup = ({
  sourceAuthorized,
  isGranolaWorkflow = false,
  granolaUseApiKey = false,
  canFinish = true,
  setupFailed = false,
}) => {
  if (setupFailed || !sourceAuthorized || !canFinish) {
    return false;
  }
  if (isGranolaWorkflow && granolaUseApiKey) {
    return false;
  }
  return true;
};

/**
 * Primary CTA label for connector setup panels.
 *
 * @example
 * getConnectorSetupPrimaryLabel({
 *   copy: getSimpleConnectCopy("zoom"),
 *   sourceAuthorized: true,
 *   shouldAutoFinish: true,
 * });
 * // "Setting up…"
 */
export const getConnectorSetupPrimaryLabel = ({
  copy,
  connectLoading = false,
  submitting = false,
  sourceAuthorized = false,
  canFinish = true,
  shouldAutoFinish = false,
  isGranolaWorkflow = false,
  granolaUseApiKey = false,
  granolaApiKeyVerified = false,
}) => {
  if (connectLoading) {
    return "Waiting for authorization…";
  }
  if (submitting || shouldAutoFinish) {
    return "Setting up…";
  }
  if (isGranolaWorkflow && granolaUseApiKey && !granolaApiKeyVerified) {
    return "Test API key";
  }
  const awaitingAuth =
    isGranolaWorkflow && granolaUseApiKey ? false : !sourceAuthorized;
  if (awaitingAuth) {
    return copy.connectCta;
  }
  if (canFinish) {
    return "Finish setup";
  }
  return copy.connectCta;
};

/**
 * Provider icon for connector setup (panel body or wizard header).
 *
 * @example
 * renderConnectorSetupIcon("zoom");
 * // panel icon in simple-connect body
 *
 * @example
 * renderConnectorSetupIcon("zoom", { placement: "header" });
 * // smaller icon beside the wizard title
 */
export const renderConnectorSetupIcon = (sourceType, options = {}) =>
  renderConnectorBrandIcon(sourceType, options);

export const WORKFLOW_SOURCE_CONFIG = {
  gmail: {
    provider: "gmail",
    providerLabel: "Gmail",
    workflowType: DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_gmail",
    title: "Connect Gmail",
    subtitle:
      "Pull meeting action items from your inbox and receive a scheduled digest by email.",
  },
  zoom: {
    provider: "zoom",
    providerLabel: "Zoom",
    workflowType: ZOOM_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_zoom",
    title: "Connect Zoom",
    subtitle:
      "Only Zoom recordings are read to find action items — nothing is edited, shared, or stored.",
  },
  google_meet: {
    provider: "google_meet",
    providerLabel: "Google Meet",
    workflowType: GOOGLE_MEET_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_google_meet",
    title: "Connect Google Meet",
    subtitle:
      "Only Meet transcripts are read to find action items — nothing is edited, shared, or stored.",
  },
  gong: {
    provider: "gong",
    providerLabel: "Gong",
    workflowType: GONG_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_gong",
    title: "Connect Gong",
    subtitle:
      "Only Gong call recordings are read to find action items — nothing is edited, shared, or stored.",
  },
  granola: {
    provider: "granola",
    providerLabel: "Granola",
    workflowType: GRANOLA_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_granola",
    title: "Connect Granola",
    subtitle:
      "Only Granola notes are read to find action items — nothing is edited, shared, or stored.",
  },
  notion: {
    provider: "notion",
    providerLabel: "Notion",
    workflowType: NOTION_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_notion",
    title: "Connect Notion",
    subtitle:
      "Only your Notion AI meeting notes are read to find action items — nothing is edited, shared, or stored.",
  },
  google_calendar: {
    provider: "google_calendar",
    providerLabel: "Google Calendar",
    workflowType: GOOGLE_CALENDAR_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_google_calendar",
    title: "Connect Google Calendar",
    subtitle:
      "Only calendar events are read to find action items — nothing is edited, shared, or stored.",
  },
  slack: {
    provider: "slack",
    providerLabel: "Slack",
    workflowType: SLACK_DIGEST_WORKFLOW_TYPE,
    oauthState: "business_slack",
    title: "Connect Slack",
    subtitle:
      "Pulls action items from your DMs and group DMs — never a whole channel unless you allow it.",
  },
};

const SOURCE_TYPE_BY_WORKFLOW_TYPE = {
  [DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE]: "gmail",
  [ZOOM_DIGEST_WORKFLOW_TYPE]: "zoom",
  [GOOGLE_MEET_DIGEST_WORKFLOW_TYPE]: "google_meet",
  [GONG_DIGEST_WORKFLOW_TYPE]: "gong",
  [GRANOLA_DIGEST_WORKFLOW_TYPE]: "granola",
  [NOTION_DIGEST_WORKFLOW_TYPE]: "notion",
  [GOOGLE_CALENDAR_DIGEST_WORKFLOW_TYPE]: "google_calendar",
  [SLACK_DIGEST_WORKFLOW_TYPE]: "slack",
};

export const WORKFLOW_TYPE_SETUP_APP_SLUG = {
  [DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE]: "email-digest",
  [ZOOM_DIGEST_WORKFLOW_TYPE]: "zoom-digest",
  [GOOGLE_MEET_DIGEST_WORKFLOW_TYPE]: "google-meet-digest",
  [GONG_DIGEST_WORKFLOW_TYPE]: "gong-digest",
  [GRANOLA_DIGEST_WORKFLOW_TYPE]: "granola-digest",
  [NOTION_DIGEST_WORKFLOW_TYPE]: "notion-digest",
  [SLACK_DIGEST_WORKFLOW_TYPE]: "slack-digest",
};

/**
 * Map a digest workflow type to its connect-apps catalog slug.
 *
 * @example
 * getSetupAppSlugForWorkflowType("email_daily_digest");
 * // "email-digest"
 */
export const getSetupAppSlugForWorkflowType = (workflowType) =>
  WORKFLOW_TYPE_SETUP_APP_SLUG[String(workflowType || "").trim()] || "";

/**
 * Resolve setup config from catalog workflow type or source key.
 *
 * @example
 * getWorkflowSourceConfigByType("zoom_daily_digest").provider;
 * // "zoom"
 */
export const getWorkflowSourceConfigByType = (workflowType) => {
  const sourceType = SOURCE_TYPE_BY_WORKFLOW_TYPE[String(workflowType || "").trim()];
  return sourceType ? WORKFLOW_SOURCE_CONFIG[sourceType] : WORKFLOW_SOURCE_CONFIG.gmail;
};

/**
 * Resolve setup config from route-style source key.
 *
 * @example
 * getWorkflowSourceConfig("granola").workflowType;
 * // "granola_daily_digest"
 */
export const getWorkflowSourceConfig = (sourceType) =>
  WORKFLOW_SOURCE_CONFIG[sourceType] || WORKFLOW_SOURCE_CONFIG.gmail;

export const createDefaultGmailRule = (overrides = {}) => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  enabled: true,
  senderPatterns: "",
  subjectKeywords: "",
  requireIcsAttachment: false,
  ...overrides,
});

/**
 * Default Gmail filter rules for the digest wizard.
 *
 * @example
 * getWorkflowFilterDefaults("email_daily_digest").gmailRules.length;
 * // 2
 */
export const getWorkflowFilterDefaults = (workflowType) => {
  if (workflowType !== DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE) {
    return {
      filterOperator: "OR",
      gmailRules: [createDefaultGmailRule()],
    };
  }

  return {
    filterOperator: "OR",
    gmailRules: [
      createDefaultGmailRule({
        name: INVITE_RULE_NAME,
        subjectKeywords: "invite,invitation,reschedule",
        requireIcsAttachment: true,
      }),
      createDefaultGmailRule({
        name: MEETING_RULE_NAME,
        senderPatterns: MEETING_DEFAULT_SENDERS,
        subjectKeywords:
          "meeting,zoom,google meet,teams,webex,agenda,standup,action items",
      }),
    ],
  };
};

export const getRuleByName = (gmailRules, name) =>
  (gmailRules || []).find((rule) => rule.name === name);

export const upsertNamedRule = (gmailRules, ruleName, updates) => {
  const rules = [...(gmailRules || [])];
  const index = rules.findIndex((rule) => rule.name === ruleName);
  if (index >= 0) {
    rules[index] = { ...rules[index], ...updates };
    return rules;
  }
  return [...rules, createDefaultGmailRule({ name: ruleName, ...updates })];
};

/**
 * Keep the calendar-invite fetch rule enabled for Gmail workflows.
 *
 * @example
 * ensureGmailInviteRuleEnabled([]);
 * // [{ name: "invite_with_ics", enabled: true, requireIcsAttachment: true, ... }]
 */
export const ensureGmailInviteRuleEnabled = (gmailRules) =>
  upsertNamedRule(gmailRules, INVITE_RULE_NAME, {
    enabled: true,
    requireIcsAttachment: true,
    subjectKeywords: WIZARD_INVITE_SUBJECT_KEYWORDS,
  });

/**
 * Build default form state for digest setup UIs.
 *
 * @example
 * createDefaultDigestFormData("email_daily_digest");
 * // { workflow_type: "email_daily_digest", frequency: "daily", lookback_days: 7, ... }
 */
export const createDefaultDigestFormData = (workflowType, provider = "") => {
  const defaults = getWorkflowFilterDefaults(workflowType);
  return {
    workflow_type: workflowType,
    frequency: "daily",
    lookback_days: DEFAULT_LOOKBACK_DAYS,
    credential_id: "",
    credential_provider: "",
    filterOperator: defaults.filterOperator,
    gmailRules: defaults.gmailRules,
    provider,
  };
};

/**
 * Extract workflow id from setup API response when present.
 *
 * @example
 * extractSetupWorkflowId({ connector_id: "629b15c8-9b47-421c-b5d1-e0ceb51c6223" });
 * // "629b15c8-9b47-421c-b5d1-e0ceb51c6223"
 */
export const extractSetupWorkflowId = (setupResponse) => {
  const topLevelId = String(
    setupResponse?.workflow_id || setupResponse?.connector_id || setupResponse?.id || ""
  ).trim();
  if (topLevelId) return topLevelId;
  return String(
    setupResponse?.workflow?.workflow_id ||
      setupResponse?.workflow?.connector_id ||
      setupResponse?.workflow?.id ||
      ""
  ).trim();
};

/**
 * Build settings URL that opens the newly connected connector after setup.
 * Lands on Connectors with that source selected so the row can show Connected
 * and Fetching… instead of sending the user to the tasks list.
 *
 * @example
 * buildDigestSetupCompleteSettingsPath("629b15c8-...", "email_daily_digest");
 * // "/business/settings?settingsTab=connect-apps&settingsApp=email-digest&settingsWorkflow=629b15c8-..."
 */
export const buildDigestSetupCompleteSettingsPath = (workflowId, workflowType) =>
  buildActionItemsSettingsPath({
    tab: ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
    app: getSetupAppSlugForWorkflowType(workflowType),
    workflow: workflowId,
  });

/**
 * Read lookback days from a workflow payload returned by list/detail APIs.
 *
 * @example
 * resolveWorkflowLookbackDays({ lookback_days: 90 });
 * // 90
 */
export const resolveWorkflowLookbackDays = (workflow, fallback = DEFAULT_LOOKBACK_DAYS) => {
  const raw =
    workflow?.lookback_days ??
    workflow?.lookbackDays ??
    workflow?.connector?.lookback_days ??
    workflow?.connector?.lookbackDays;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Resolve the connector id backing a business workflow row.
 *
 * @example
 * resolveWorkflowConnectorId({ workflow_id: "629b15c8-9b47-421c-b5d1-e0ceb51c6223" });
 * // "629b15c8-9b47-421c-b5d1-e0ceb51c6223"
 */
export const resolveWorkflowConnectorId = (workflow) => {
  const candidates = [
    workflow?.connector_id,
    workflow?.connector?.id,
    workflow?.workflow_id,
  ];
  return String(candidates.find((id) => String(id || "").trim()) || "").trim();
};

/**
 * Merge saved workflow fields into a workflows list by id.
 *
 * @example
 * mergeWorkflowIntoList([{ workflow_id: "wf-1", lookback_days: 30 }], "wf-1", { lookback_days: 90 });
 * // [{ workflow_id: "wf-1", lookback_days: 90 }]
 */
export const mergeWorkflowIntoList = (workflows, workflowId, patch = {}) => {
  const id = String(workflowId || "").trim();
  if (!id) return Array.isArray(workflows) ? workflows : [];
  return (Array.isArray(workflows) ? workflows : []).map((workflow) =>
    String(workflow?.workflow_id || "").trim() === id ? { ...workflow, ...patch } : workflow
  );
};

/**
 * Remove one workflow from a list by id.
 *
 * @example
 * removeWorkflowFromList(
 *   [{ workflow_id: "wf-1" }, { workflow_id: "wf-2" }],
 *   "wf-1"
 * );
 * // [{ workflow_id: "wf-2" }]
 */
export const removeWorkflowFromList = (workflows, workflowId) => {
  const id = String(workflowId || "").trim();
  if (!id) return Array.isArray(workflows) ? workflows : [];
  return (Array.isArray(workflows) ? workflows : []).filter(
    (workflow) => String(workflow?.workflow_id || "").trim() !== id
  );
};

/**
 * Build POST body for gmail/zoom/granola setup endpoints.
 *
 * @example
 * buildDigestSetupPayload({
 *   sourceConfig: getWorkflowSourceConfig("zoom"),
 *   formData: createDefaultDigestFormData("zoom_daily_digest"),
 *   scheduleTimezone: "UTC",
 * });
 */
export const buildDigestSetupPayload = ({
  sourceConfig,
  formData,
  scheduleTimezone,
  granolaApiKey = "",
  granolaUseApiKey = false,
  connectorName = "",
}) => {
  const isEmailWorkflow = sourceConfig.workflowType === DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE;
  const isGranolaWorkflow = sourceConfig.provider === "granola";
  const payload = {
    workflow_type: sourceConfig.workflowType,
    frequency: formData.frequency,
    lookback_days: formData.lookback_days,
    credential_id: formData.credential_id,
    schedule_timezone: scheduleTimezone,
  };
  if (isGranolaWorkflow && granolaUseApiKey) {
    payload.api_key = String(granolaApiKey || "").trim();
  }
  if (isEmailWorkflow) {
    payload.filters = buildWizardGmailFiltersPayload(
      formData.workflow_type,
      formData.filterOperator,
      ensureGmailInviteRuleEnabled(formData.gmailRules)
    );
  }
  if (sourceConfig.provider === "slack") {
    // v1 filter policy: DMs + group DMs on by default, channel allowlist
    // empty until a settings UI exists to pick channels post-connect.
    payload.filters = {
      include_dms: formData.slackIncludeDms !== false,
      include_group_dms: formData.slackIncludeGroupDms !== false,
      channel_allowlist: Array.isArray(formData.slackChannelAllowlist)
        ? formData.slackChannelAllowlist
        : [],
    };
  }
  if (!payload.credential_id) {
    delete payload.credential_id;
  }
  const trimmedName = String(connectorName || "").trim();
  if (trimmedName) {
    payload.connector_name = trimmedName;
  }
  return payload;
};

/**
 * Pick a connector name that is not already used (case-insensitive).
 *
 * @example
 * uniqueConnectorName("Google Meet", ["google meet"]);
 * // "Google Meet (2)"
 */
export const uniqueConnectorName = (base, existingNames = []) => {
  const taken = new Set(
    (existingNames || []).map((name) => String(name || "").trim().toLowerCase())
  );
  const trimmed = String(base || "").trim();
  if (!taken.has(trimmed.toLowerCase())) return trimmed;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${trimmed} (${n})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return trimmed;
};

/**
 * Suggested connector name: the authorised account when the provider told us
 * which one it was, else the provider label. Always unique among `existingNames`.
 *
 * @example
 * suggestConnectorName("Google Meet", "sam@acme.com", []);
 * // "Google Meet - sam@acme.com"
 */
export const suggestConnectorName = (providerLabel, accountEmail = "", existingNames = []) => {
  const label = String(providerLabel || "").trim() || "Connector";
  const email = String(accountEmail || "").trim();
  return uniqueConnectorName(email ? `${label} - ${email}` : label, existingNames);
};

/**
 * Validation message for a connector name, or "" when it is fine.
 *
 * @example
 * validateConnectorName("Notion", ["notion"]);
 * // "You already have a connector named “Notion”."
 */
export const validateConnectorName = (name, existingNames = []) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "Give this connector a name.";
  const taken = (existingNames || []).some(
    (existing) => String(existing || "").trim().toLowerCase() === trimmed.toLowerCase()
  );
  return taken ? `You already have a connector named “${trimmed}”.` : "";
};

/** MCP providers whose auth URL already carries its own state (PKCE pending id). */
const MCP_OAUTH_PROVIDERS = new Set(["granola", "notion"]);

/**
 * Fetch OAuth authorization URL for a business provider.
 *
 * @example
 * const { authUrl } = await fetchProviderAuthUrl("gmail", "business_gmail");
 * // authUrl includes state=business_gmail
 */
export const fetchProviderAuthUrl = async (provider, oauthState) => {
  const authUrlPath =
    provider === "gmail"
      ? `${config.BACKEND_URL}/externalauth/${provider}/auth_url?purpose=workflow`
      : MCP_OAUTH_PROVIDERS.has(provider)
        ? `${config.BACKEND_URL}/externalauth/${provider}/auth_url?oauth_state=${encodeURIComponent(oauthState)}`
        : `${config.BACKEND_URL}/externalauth/${provider}/auth_url`;
  let payload;
  try {
    payload = await fetchJsonDesktopSafe(authUrlPath);
  } catch (error) {
    throw new Error(
      String(error?.message || "").trim() ||
        `Failed to initialize ${provider} authorization`
    );
  }
  if (MCP_OAUTH_PROVIDERS.has(provider)) {
    return { authUrl: payload.auth_url };
  }
  const authUrl = new URL(payload.auth_url);
  authUrl.searchParams.set("state", oauthState);
  return { authUrl: authUrl.toString() };
};

/**
 * Exchange OAuth code for credential_id.
 *
 * @example
 * const { credentialId } = await completeProviderOAuth("zoom", "abc123");
 * // credentialId => "cred_xyz"
 */
export const completeProviderOAuth = async (
  provider,
  code,
  { purpose, state, existingCredentialId } = {}
) => {
  return exchangeOAuthCodeOnce(provider, code, async (authCode) => {
    const payload = { code: authCode };
    if (provider === "gmail" && purpose) {
      payload.purpose = purpose;
    }
    if (state) {
      payload.state = state;
    }
    const credentialId = String(existingCredentialId || "").trim();
    if (credentialId) {
      payload.existing_credential_id = credentialId;
    }
    const data = await postExternalAuthCallback(provider, payload);
    return {
      credentialId: String(data.credential_id || "").trim(),
      message: data.message,
      meetApiReady: data.meet_api_ready,
      // The Google account that was authorised, when the provider reports it.
      accountEmail: String(data.account_email || "").trim(),
    };
  });
};

/** Business connectors settings tab — default return path after OAuth cancel. */
export const BUSINESS_CONNECTORS_SETTINGS_PATH =
  "/business/settings?settingsTab=connect-apps";

/** Business workflow/CRM setup routes keyed by OAuth state (legacy /sources/add success redirect). */
export const BUSINESS_OAUTH_ROUTES_BY_STATE = {
  business_gmail: "/business/workflows/new/email-digest",
  business_zoom: "/business/workflows/new/zoom-digest",
  business_google_meet: "/business/workflows/new/google-meet-digest",
  business_gong: "/business/workflows/new/gong-digest",
  business_granola: "/business/workflows/new/granola-digest",
  business_notion: "/business/workflows/new/notion-digest",
  business_google_calendar: "/business/workflows/new/calendar-digest",
  business_slack: "/business/workflows/new/slack-digest",
  business_attio: "/business/workflows/new/attio",
  business_salesforce: "/business/workflows/new/salesforce",
};

/** CRM write-back OAuth states (not digest workflows) that return via /sources/add. */
export const BUSINESS_CRM_OAUTH_STATES = new Set([
  "business_attio",
  "business_salesforce",
]);

export const BUSINESS_OAUTH_STATES = new Set([
  ...Object.values(WORKFLOW_SOURCE_CONFIG).map((item) => item.oauthState),
  ...BUSINESS_CRM_OAUTH_STATES,
]);

/** Suffix appended to an oauthState when the OAuth flow runs in a popup tab. */
export const BUSINESS_OAUTH_POPUP_STATE_SUFFIX = "_popup";

/**
 * Extra suffix when the popup was opened from the Electron app.
 * Chrome and Electron do not share localStorage, so the callback tab hands the
 * code to the app via /desktop-callback -> taskmantis:// instead of writing BUSINESS_OAUTH_POPUP_RESULT_KEY.
 */
export const BUSINESS_OAUTH_DESKTOP_STATE_SUFFIX = DESKTOP_STATE_SUFFIX;

/** localStorage key used by the OAuth popup tab to hand its result back to the opener. */
export const BUSINESS_OAUTH_POPUP_RESULT_KEY = "business_oauth_popup_result_v1";

/**
 * Same-window event for Electron: Chrome and Electron do not share localStorage,
 * so the desktop deep-link handler publishes the result here after exchanging the code.
 */
export const BUSINESS_OAUTH_POPUP_RESULT_EVENT = "starmesh-oauth-popup-result";

/**
 * Resolve the integration provider for a business OAuth state.
 *
 * @example
 * getBusinessOAuthProvider("business_granola");
 * // "granola"
 */
export const getBusinessOAuthProvider = (oauthBaseState) => {
  const workflowConfig = Object.values(WORKFLOW_SOURCE_CONFIG).find(
    (item) => item.oauthState === oauthBaseState
  );
  if (workflowConfig) {
    return workflowConfig.provider;
  }
  if (oauthBaseState === "business_attio") {
    return "attio";
  }
  if (oauthBaseState === "business_salesforce") {
    return "salesforce";
  }
  return "";
};

/**
 * True when this renderer is the Electron desktop shell.
 *
 * @example
 * isTaskMantisDesktopRenderer();
 * // true inside the packaged/dev Electron window, false in Chrome
 */
export const isTaskMantisDesktopRenderer = () =>
  Boolean(typeof window !== "undefined" && window.starmeshDesktop?.isElectron);

/**
 * Build the `state` value used when an OAuth flow opens in a popup tab.
 *
 * In Electron the extra `_desktop2` marker tells the Chrome callback tab to
 * hand the code to the app via /desktop-callback instead of writing localStorage.
 * (Apps <= 0.1.13 emitted `_desktop`, which still bounces to the loopback.)
 *
 * @example
 * toPopupOauthState("business_gmail");
 * // "business_gmail_popup" in the browser
 *
 * @example
 * // inside Electron
 * toPopupOauthState("business_gong");
 * // "business_gong_popup_desktop2"
 */
export const toPopupOauthState = (oauthState) => {
  const popupState = `${oauthState}${BUSINESS_OAUTH_POPUP_STATE_SUFFIX}`;
  if (isTaskMantisDesktopRenderer()) {
    return `${popupState}${BUSINESS_OAUTH_DESKTOP_STATE_SUFFIX}`;
  }
  return popupState;
};

/**
 * Append `_desktop2` when OAuth is started from Electron (performer Gmail/Drive/Calendar
 * use a bare state, not the popup suffix).
 *
 * @example
 * tagDesktopOauthState("performer_gmail");
 * // "performer_gmail_desktop2" inside Electron, otherwise "performer_gmail"
 */
export const tagDesktopOauthState = (oauthState) => {
  const value = String(oauthState || "");
  if (!value || isDesktopOauthReturn(value)) {
    return value;
  }
  if (isTaskMantisDesktopRenderer()) {
    return `${value}${BUSINESS_OAUTH_DESKTOP_STATE_SUFFIX}`;
  }
  return value;
};

/**
 * Strip the Electron `_desktop2` (or legacy `_desktop`) marker from an OAuth `state` value, if present.
 *
 * @example
 * stripDesktopOauthState("business_gong_popup_desktop");
 * // "business_gong_popup"
 */
export const stripDesktopOauthState = (state) => {
  const value = String(state || "");
  // `_desktop2` first: `_desktop` is not a suffix of it, but keep the order explicit.
  for (const suffix of [DESKTOP_STATE_SUFFIX, LEGACY_DESKTOP_STATE_SUFFIX]) {
    if (value.endsWith(suffix)) {
      return value.slice(0, -suffix.length);
    }
  }
  return value;
};

/**
 * Strip the popup (and desktop) suffix from an OAuth `state` value, if present.
 *
 * @example
 * stripPopupOauthState("business_gmail_popup");
 * // "business_gmail"
 *
 * @example
 * stripPopupOauthState("business_gong_popup_desktop");
 * // "business_gong"
 *
 * @example
 * stripPopupOauthState("business_zoom");
 * // "business_zoom"
 */
export const stripPopupOauthState = (state) => {
  const withoutDesktop = stripDesktopOauthState(state);
  return withoutDesktop.endsWith(BUSINESS_OAUTH_POPUP_STATE_SUFFIX)
    ? withoutDesktop.slice(0, -BUSINESS_OAUTH_POPUP_STATE_SUFFIX.length)
    : withoutDesktop;
};

/**
 * True when this OAuth `state` was started from the Electron app (current
 * `_desktop2` or legacy `_desktop` marker).
 *
 * @example
 * isDesktopOauthReturn("business_gong_popup_desktop2.uuid");
 * // true
 *
 * @example
 * isDesktopOauthReturn("business_gong_popup_desktop.uuid");
 * // true (legacy app)
 *
 * @example
 * isDesktopOauthReturn("business_gong_popup");
 * // false
 */
export const isDesktopOauthReturn = (state) =>
  isDesktopState(state) || isLegacyDesktopState(state);

/**
 * If this Chrome tab is the desktop-app OAuth callback, move it to the clean
 * `/desktop-callback` page (which opens the app via `taskmantis://`) and stop
 * rendering. No-op inside Electron, on the callback page itself, or for
 * ordinary browser popups (those still use localStorage).
 *
 * Must run synchronously during render — a useEffect is too late because the
 * wizard would exchange (and consume) the one-time code first.
 *
 * @example
 * // Chrome tab at /business/workflows/new/gong-digest?code=abc&state=business_gong_popup_desktop
 * bounceDesktopOauthCallbackIfNeeded();
 * // window.location becomes https://app.taskmantis.com/desktop-callback?code=abc&state=...
 * // (the page then strips the query string and opens taskmantis://oauth-callback?...)
 *
 * @returns {boolean} true when a bounce was started
 */
export const bounceDesktopOauthCallbackIfNeeded = () => {
  if (typeof window === "undefined" || isTaskMantisDesktopRenderer()) {
    return false;
  }
  if (window.location.pathname === DESKTOP_CALLBACK_PATH) {
    return false;
  }
  const payload = readDesktopCallbackParams(window.location.search);
  if (!isDesktopOauthReturn(payload.state)) {
    return false;
  }
  if (!payload.code && !payload.error) {
    return false;
  }
  // Apps <= 0.1.13 (`_desktop`) only listen on the loopback and cannot open
  // taskmantis://, so they keep the old redirect. Current apps (`_desktop2`)
  // end on the trusted /desktop-callback page.
  if (isLegacyDesktopState(payload.state)) {
    const loopback = new URL(LEGACY_DESKTOP_LOOPBACK_URL);
    if (payload.code) loopback.searchParams.set("code", payload.code);
    if (payload.state) loopback.searchParams.set("state", payload.state);
    if (payload.error) loopback.searchParams.set("error", payload.error);
    window.location.replace(loopback.toString());
    return true;
  }
  window.location.replace(
    buildDesktopCallbackPageUrl(window.location.origin, payload)
  );
  return true;
};

/**
 * Publish a popup-OAuth result to the opener. Uses localStorage for a real
 * browser popup tab, plus a same-window CustomEvent for Electron (storage
 * events do not fire in the window that writes them).
 *
 * @example
 * publishBusinessOAuthPopupResult({ provider: "gong", credentialId: "cred_1" });
 */
export const publishBusinessOAuthPopupResult = (result) => {
  if (typeof window === "undefined" || !result || typeof result !== "object") {
    return;
  }
  try {
    window.localStorage.setItem(
      BUSINESS_OAUTH_POPUP_RESULT_KEY,
      JSON.stringify(result)
    );
  } catch {
    // Private mode / quota — still dispatch the in-window event.
  }
  window.dispatchEvent(
    new CustomEvent(BUSINESS_OAUTH_POPUP_RESULT_EVENT, { detail: result })
  );
};

/**
 * Listen for a popup OAuth result from another tab (storage) or this window
 * (custom event). Filters by `provider` and clears the localStorage key.
 *
 * @example
 * const stop = subscribeBusinessOAuthPopupResult("gong", (result) => {});
 * stop();
 */
export const subscribeBusinessOAuthPopupResult = (provider, onResult) => {
  const consume = (raw) => {
    let result = raw;
    if (typeof raw === "string") {
      try {
        result = JSON.parse(raw);
      } catch {
        return;
      }
    }
    if (!result || typeof result !== "object") return;
    if (String(result.provider || "") !== String(provider || "")) return;
    try {
      window.localStorage.removeItem(BUSINESS_OAUTH_POPUP_RESULT_KEY);
    } catch {
      // ignore
    }
    onResult(result);
  };

  const handleStorage = (event) => {
    if (event.key !== BUSINESS_OAUTH_POPUP_RESULT_KEY || !event.newValue) return;
    consume(event.newValue);
  };
  const handleLocal = (event) => consume(event.detail);

  window.addEventListener("storage", handleStorage);
  window.addEventListener(BUSINESS_OAUTH_POPUP_RESULT_EVENT, handleLocal);
  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(BUSINESS_OAUTH_POPUP_RESULT_EVENT, handleLocal);
  };
};

const PERFORMER_OAUTH_STATES = new Set([
  "performer_gmail",
  "performer_drive",
  "performer_calendar",
  "performer_slack",
]);

/**
 * Map a loopback OAuth payload onto the in-app callback URL the existing
 * wizards already know how to finish (same-window, not popup-handoff).
 *
 * Strips `_popup` / `_desktop` so BusinessDigestWizard / AttioConnectPanel
 * take the in-window success path instead of window.close().
 *
 * @example
 * resolveDesktopLoopbackTarget(
 *   { code: "abc", state: "business_gong_popup_desktop" },
 *   "/business/workflows/new/gong-digest",
 *   "?reconnect=wf-1"
 * );
 * // { pathname: "/business/workflows/new/gong-digest", search: "?reconnect=wf-1&code=abc&state=business_gong" }
 */
export const resolveDesktopLoopbackTarget = (
  payload,
  currentPathname = "",
  currentSearch = ""
) => {
  const code = String(payload?.code || "").trim();
  const oauthError = String(payload?.error || "").trim();
  const rawState = String(payload?.state || "").trim();
  if (isDesktopLoginState(rawState)) {
    return null;
  }
  const baseState = resolveBusinessOauthState(rawState);
  if (!baseState) {
    return null;
  }
  if (!code && !oauthError) {
    return null;
  }

  const params = new URLSearchParams(
    String(currentSearch || "").startsWith("?")
      ? currentSearch.slice(1)
      : currentSearch || ""
  );
  if (code) {
    params.set("code", code);
  } else {
    params.delete("code");
  }
  params.set("state", baseState);
  if (oauthError) {
    params.set("error", oauthError);
  } else {
    params.delete("error");
  }

  let pathname = String(currentPathname || "").trim() || "/";
  if (PERFORMER_OAUTH_STATES.has(baseState)) {
    if (!pathname.startsWith("/business")) {
      pathname = "/business/settings";
      if (!params.get("settingsTab")) {
        params.set("settingsTab", "general");
      }
    }
  } else {
    const setupPath = BUSINESS_OAUTH_ROUTES_BY_STATE[baseState];
    if (setupPath && pathname !== setupPath) {
      pathname = setupPath;
    }
  }

  const qs = params.toString();
  return { pathname, search: qs ? `?${qs}` : "" };
};

export const resolveBusinessOauthState = (state) => {
  const withoutPending = String(state || "").split(".")[0];
  return stripPopupOauthState(withoutPending);
};

export const isPopupOauthReturn = (state, oauthState) => {
  const statePrefix = String(state || "").split(".")[0];
  return (
    stripPopupOauthState(statePrefix) === oauthState && statePrefix !== oauthState
  );
};

/**
 * Parse a legacy `/sources/add` OAuth callback that has no authorization code.
 * Used when the user cancels or denies access in the provider consent screen.
 *
 * @example
 * parseBusinessOAuthLegacyCallback("?error=access_denied&state=business_granola_popup.uuid");
 * // { oauthBaseState: "business_granola", provider: "granola", isPopupReturn: true, cancelled: true }
 *
 * @example
 * parseBusinessOAuthLegacyCallback("?code=abc&state=business_granola");
 * // null
 */
export const parseBusinessOAuthLegacyCallback = (search) => {
  const params = new URLSearchParams(String(search || ""));
  if (params.get("code")) {
    return null;
  }
  const state = params.get("state");
  if (!state) {
    return null;
  }
  const oauthBaseState = resolveBusinessOauthState(state);
  if (!BUSINESS_OAUTH_STATES.has(oauthBaseState)) {
    return null;
  }
  const provider = getBusinessOAuthProvider(oauthBaseState);
  if (!provider) {
    return null;
  }
  return {
    oauthBaseState,
    provider,
    isPopupReturn: isPopupOauthReturn(state, oauthBaseState),
    cancelled: true,
  };
};

/**
 * Hand a cancelled popup OAuth flow back to the opener via localStorage, then close.
 *
 * @example
 * handoffBusinessOAuthPopupCancel("granola");
 * // writes { provider: "granola", cancelled: true } and calls window.close()
 */
export const handoffBusinessOAuthPopupCancel = (provider) => {
  if (typeof window === "undefined" || !provider) {
    return;
  }
  window.localStorage.setItem(
    BUSINESS_OAUTH_POPUP_RESULT_KEY,
    JSON.stringify({ provider, cancelled: true })
  );
  window.history.replaceState({}, "", window.location.pathname);
  window.close();
};

/**
 * True when the URL carries an OAuth code for this workflow provider.
 *
 * @example
 * isPendingBusinessOAuthCallback("?code=abc&state=business_granola.uuid", "business_granola");
 * // true
 */
export const isPendingBusinessOAuthCallback = (search, oauthState) => {
  const params = new URLSearchParams(String(search || ""));
  if (!params.get("code")) {
    return false;
  }
  return resolveBusinessOauthState(params.get("state")) === oauthState;
};

/**
 * True when this tab can post OAuth results back to an opener popup flow.
 *
 * @example
 * canHandoffOAuthToOpener();
 * // true in a popup tab with window.opener set
 */
export const canHandoffOAuthToOpener = () =>
  typeof window !== "undefined" && Boolean(window.opener) && !window.opener.closed;

/**
 * Provider hints for filter summary chips.
 *
 * @example
 * extractProviderHint("no-reply@zoom.us");
 * // "Zoom"
 * extractProviderHint("gemini-notes@google.com");
 * // "Google Meet"
 */
export const extractProviderHint = (senderPattern) => {
  const value = String(senderPattern || "").toLowerCase().trim();
  if (!value) return "";
  if (value.includes("zoom")) return "Zoom";
  if (value.includes("teams.microsoft") || value.includes("microsoft")) return "Microsoft Teams";
  if (value.includes("webex")) return "Webex";
  if (value.includes("gemini-notes") || value.includes("meet.google")) return "Google Meet";
  if (value.includes("google")) return "Google Calendar";

  const domainCandidate = value.includes("@") ? value.split("@").pop() : value;
  const domain = String(domainCandidate || "")
    .replace(/^www\./, "")
    .split(/[/:]/)[0];
  const domainParts = domain.split(".").filter(Boolean);
  if (domainParts.length >= 3) {
    const qualifier = domainParts[0];
    const brand = domainParts[1];
    return `${brand.charAt(0).toUpperCase() + brand.slice(1)} ${qualifier.charAt(0).toUpperCase() + qualifier.slice(1)}`.trim();
  }
  const root = domainParts[0] || value;
  return root.charAt(0).toUpperCase() + root.slice(1).toLowerCase();
};

export const buildSenderFilterChips = (meetingRule) => {
  const senderValues = splitCommaValues(meetingRule?.senderPatterns || MEETING_DEFAULT_SENDERS);
  const seen = new Set();
  return senderValues
    .map((sender) => extractProviderHint(sender))
    .filter((label) => {
      const key = String(label || "").toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const buildKeywordFilterChips = (meetingRule) =>
  splitCommaValues(meetingRule?.subjectKeywords || MEETING_DEFAULT_KEYWORDS);

/**
 * Tab label for a digest wizard step (varies by source type).
 *
 * @example
 * getWizardStepTabLabel("workflow", { isGranolaWorkflow: false });
 * // "Setup filters"
 *
 * @example
 * getWizardStepTabLabel("sync", { isGranolaWorkflow: true });
 * // "API key"
 */
export const getWizardStepTabLabel = (stepId, { isGranolaWorkflow, granolaUseApiKey }) => {
  if (stepId === "workflow") return "Setup filters";
  if (isGranolaWorkflow && granolaUseApiKey) return "API key";
  return "Authenticate";
};

/**
 * OAuth status label for connected data source settings.
 *
 * @example
 * getAuthConnectedLabel("email_daily_digest");
 * // "Gmail read access connected"
 *
 * @example
 * getAuthConnectedLabel("google_calendar_daily_digest");
 * // "Google Calendar connected"
 */
export const getAuthConnectedLabel = (workflowType) => {
  const normalized = String(workflowType || "").trim();
  if (normalized === DEFAULT_EMAIL_DIGEST_WORKFLOW_TYPE) {
    return "Gmail read access connected";
  }
  const sourceConfig = getWorkflowSourceConfigByType(normalized);
  return `${sourceConfig.providerLabel} connected`;
};

/**
 * Build setup URL that re-authenticates an existing workflow connector.
 *
 * @example
 * buildReconnectSetupPath("/business/workflows/new/zoom-digest", "wf-1", "cred-1");
 * // "/business/workflows/new/zoom-digest?reconnect=wf-1&credential_id=cred-1"
 */
export const buildReconnectSetupPath = (setupPath, workflowId, credentialId = "") => {
  const path = String(setupPath || "").trim();
  const workflow = String(workflowId || "").trim();
  if (!path || !workflow) {
    return path;
  }
  const params = new URLSearchParams({ reconnect: workflow });
  const credential = String(credentialId || "").trim();
  if (credential) {
    params.set("credential_id", credential);
  }
  return `${path}?${params.toString()}`;
};

/**
 * Read reconnect workflow id from setup URL search params.
 *
 * @example
 * parseReconnectWorkflowId("?reconnect=wf-1&credential_id=cred-1");
 * // "wf-1"
 */
export const parseReconnectWorkflowId = (search = "") =>
  String(new URLSearchParams(String(search || "")).get("reconnect") || "").trim();

/**
 * Read existing credential id from reconnect setup URL search params.
 *
 * @example
 * parseReconnectCredentialId("?reconnect=wf-1&credential_id=cred-1");
 * // "cred-1"
 */
export const parseReconnectCredentialId = (search = "") =>
  String(new URLSearchParams(String(search || "")).get("credential_id") || "").trim();

/**
 * Persist reconnect context for OAuth popup handoff.
 *
 * @example
 * persistReconnectContext({ workflowId: "wf-1", credentialId: "cred-1" });
 */
export const persistReconnectContext = ({ workflowId, credentialId } = {}) => {
  if (typeof window === "undefined") {
    return;
  }
  const workflow = String(workflowId || "").trim();
  if (!workflow) {
    window.sessionStorage.removeItem(BUSINESS_RECONNECT_CONTEXT_KEY);
    return;
  }
  window.sessionStorage.setItem(
    BUSINESS_RECONNECT_CONTEXT_KEY,
    JSON.stringify({
      workflowId: workflow,
      credentialId: String(credentialId || "").trim(),
    })
  );
};

/**
 * Read reconnect context saved before OAuth popup opens.
 *
 * @example
 * readReconnectContext();
 * // { workflowId: "wf-1", credentialId: "cred-1" }
 */
export const readReconnectContext = () => {
  if (typeof window === "undefined") {
    return null;
  }
  const raw = window.sessionStorage.getItem(BUSINESS_RECONNECT_CONTEXT_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    const workflowId = String(parsed?.workflowId || "").trim();
    if (!workflowId) {
      return null;
    }
    return {
      workflowId,
      credentialId: String(parsed?.credentialId || "").trim(),
    };
  } catch {
    return null;
  }
};

export const clearReconnectContext = () => {
  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(BUSINESS_RECONNECT_CONTEXT_KEY);
  }
};

/**
 * True when a connector sync error likely needs OAuth reconnect.
 *
 * @example
 * connectorErrorLooksLikeAuth("Zoom refresh token invalid or expired — user must re-authenticate.");
 * // true
 */
export const connectorErrorLooksLikeAuth = (errorMsg) => {
  if (!errorMsg || typeof errorMsg !== "string") {
    return false;
  }
  const normalized = errorMsg.toLowerCase();
  return (
    normalized.includes("invalid_grant") ||
    normalized.includes("token expired") ||
    normalized.includes("token has been expired") ||
    normalized.includes("refresh token invalid") ||
    normalized.includes("must re-authenticate") ||
    normalized.includes("re-authenticate") ||
    normalized.includes("revoked") ||
    normalized.includes("unauthorized") ||
    normalized.includes("invalid credentials") ||
    normalized.includes("insufficient authentication") ||
    normalized.includes("access_denied")
  );
};

/**
 * Resolve the best sync error message to show in connector settings.
 *
 * @example
 * resolveWorkflowSyncError({ latest_sync_error: "token expired", first_run_error: null });
 * // "token expired"
 */
export const resolveWorkflowSyncError = (workflow) => {
  const latest = String(workflow?.latest_sync_error || "").trim();
  if (latest) {
    return latest;
  }
  return String(workflow?.first_run_error || "").trim();
};

/**
 * Whether connector settings should prompt the user to reconnect OAuth.
 *
 * @example
 * workflowNeedsReconnect({ needs_reauth: true });
 * // true
 */
export const workflowNeedsReconnect = (workflow) => {
  if (!workflow) {
    return false;
  }
  if (workflow.needs_reauth === true) {
    return true;
  }
  return connectorErrorLooksLikeAuth(resolveWorkflowSyncError(workflow));
};
